import { createBrowserRouter, Navigate, type RouteObject } from 'react-router-dom';

import { LEGAL_DOCUMENTS } from '@/features/legal/documents';

import { AccountPage } from './AccountPage';
import { HomePage } from './HomePage';
import { LegalPage } from './LegalPage';
import { NotFoundPage } from './NotFoundPage';
import { MyOrdersPage } from './MyOrdersPage';
import { OrderTrackingPage } from './OrderTrackingPage';
import { SignInPage } from './SignInPage';
import { BasketStep } from './order/BasketStep';
import { DetailsStep } from './order/DetailsStep';
import { OrderLayout } from './order/OrderLayout';
import { PayStep } from './order/PayStep';
import { RetiredStep } from './order/RetiredStep';
import { RouteErrorBoundary } from './RouteErrorBoundary';
import { paths } from './paths';

/**
 * Route table. Customer surfaces only.
 *
 * Each page renders its own AppShell, because the title, stepper and sticky
 * action differ per step.
 *
 * THERE IS NO `/admin` HERE ANY MORE. The operator console is a desktop
 * application (`desktop/`), so this bundle does not carry the queue, the
 * catalogue editor or the admin API client, and printoman.in serves no operator
 * login form. Note what that does NOT do: the admin API is still on the public
 * internet and still reachable by anything holding a token. Moving the UI off
 * the web shrinks what we ship, not what we expose — see DECISIONS.
 *
 * Every checkout step is its own path — see routes/order/steps.ts for why progress
 * lives in the URL rather than in component state.
 *
 * Code splitting is deliberately absent: what remains is one linear checkout
 * flow plus a handful of leaf pages, and lazy routes would add suspense
 * boundaries with very little behind them.
 */
export const routes: RouteObject[] = [
  {
    path: paths.home,
    element: <HomePage />,
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: paths.order.root,
    element: <OrderLayout />,
    errorElement: <RouteErrorBoundary />,
    children: [
      { index: true, element: <BasketStep /> },
      { path: 'details', element: <DetailsStep /> },
      { path: 'pay', element: <PayStep /> },
    ],
  },
  /*
   * Retired steps, answered with what replaced them. See RetiredStep.
   *
   * OUTSIDE the OrderLayout above, and that is load-bearing: the layout
   * guards /order/* against paths it recognises and redirects anything else
   * to the start of the flow, so a retired route nested inside it would be
   * swallowed by the guard before its redirect ever rendered — and
   * /order/address would land on the basket rather than on Details with the
   * order it was carrying.
   */
  ...(
    [
      ['upload', 'basket'],
      ['review', 'basket'],
      ['options', 'basket'],
      ['address', 'details'],
    ] as const
  ).map(([dead, live]) => ({
    path: `/order/${dead}`,
    element: <RetiredStep to={live} />,
    errorElement: <RouteErrorBoundary />,
  })),
  {
    // The basket used to live here while it was being built alongside the old
    // flow, and `/cart` before that. Both are now the first step of /order.
    path: '/basket',
    element: <Navigate to={paths.order.root} replace />,
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: '/cart',
    element: <Navigate to={paths.order.root} replace />,
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: paths.signIn,
    element: <SignInPage />,
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: paths.orders,
    element: <MyOrdersPage />,
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: paths.account,
    element: <AccountPage />,
    errorElement: <RouteErrorBoundary />,
  },
  {
    // Where the payment gateway returns the customer. The order number is the
    // only handle they carry back, which is why the API has a by-number lookup.
    path: '/orders/:orderNo',
    element: <OrderTrackingPage />,
    errorElement: <RouteErrorBoundary />,
  },
  /**
   * The policy documents, generated from the same list the footer renders — a
   * new policy is one entry in features/legal/documents.ts plus one path, and
   * it cannot end up linked-but-404 or reachable-but-unlinked.
   */
  ...LEGAL_DOCUMENTS.map((doc) => ({
    path: paths.legal[doc.slug],
    element: <LegalPage slug={doc.slug} />,
    errorElement: <RouteErrorBoundary />,
  })),
  { path: '*', element: <NotFoundPage /> },
];

export const router = createBrowserRouter(routes);
